// useUserPosts hook for fetching user's blog (posts + resteems)

import { useInfiniteQuery } from '@tanstack/react-query'
import { getAccountBlog } from '@/services/feeds/feedsClient'
import { transformFeedResponse } from '@/services/feeds/transformer'
import { queryKeys } from '@/services/queryClient'
import { Post } from '@/types/post'

interface UseUserPostsOptions {
  username: string
  limit?: number
  enabled?: boolean
}

export function useUserPosts({ username, limit = 20, enabled = true }: UseUserPostsOptions) {
  return useInfiniteQuery({
    queryKey: queryKeys.userPosts(username),
    queryFn: async ({ pageParam = 0 }) => {
      const response = await getAccountBlog(username, {
        offset: pageParam,
        limit,
        bodyLength: 250,
      })

      const rows = response?.result?.rows || []
      const posts = transformFeedResponse(rows)

      return {
        posts,
        nextOffset: pageParam + limit,
        hasMore: posts.length === limit,
      }
    },
    getNextPageParam: (lastPage) => {
      return lastPage.hasMore ? lastPage.nextOffset : undefined
    },
    initialPageParam: 0,
    enabled: enabled && !!username,
    staleTime: 60 * 1000, // 1 minute
  })
}

export function getAllUserPosts(
  data: { pages: { posts: Post[] }[] } | undefined
): Post[] {
  if (!data) return []
  return data.pages.flatMap((page) => page.posts)
}
